// client/src/dnd/emitter_tool/engine/GlowManager.js
import * as PIXI from "pixi.js";
import {
  shouldParticleGlow,
  computeGlowParams,
  dispatchGlowPulse,
  computePerParticleCooldownS,
} from "./engine_parts/glow";

const NOW_S = () => performance.now() / 1000;

// Utility: per-particle cooldown, balanced between base rate and duration
function cooldownWithDuration(intensity01, durationS) {
  const base = computePerParticleCooldownS(intensity01);
  const dur = Math.max(0, Number(durationS) || 0);
  if (!dur) return base;

  return base; 
}

export default class GlowManager {
  /**
   * @param {Object} options
   * @param {PIXI.Container} [options.cutContainer]  Container that sits ABOVE your fog graphic in the same subtree.
   * @param {boolean} [options.useEraseBlend=true]   Use ERASE/DST_OUT to punch holes into fog.
   * @param {boolean} [options.debugDrawCircles=false] Also draw visible white circles (debug only).
   * @param {string}  [options.glowEventName="fx-glow"] Global event name used to dispatch pulses.
   * @param {number}  [options.pxPerMeter=40] Grid scale for radiusM→px conversion.
   * @param {number}  [options.square_length=null] Optional grid square length for parity calcs.
   * @param {PIXI.Application} [options.app] Optional PIXI app; if provided we’ll try to auto-find FogHoles.
   * @param {string}  [options.configEventName="EmitterTool:GlowConfig"] Window event name your UI dispatches.
   * @param {boolean} [options.debug=false]
   */
  constructor(options = {}) {
    this._glowEventName   = options.glowEventName || "fx-glow";
    this._configEventName = options.configEventName || "EmitterTool:GlowConfig";

    this._pxPerMeter    = Number.isFinite(options.pxPerMeter) ? options.pxPerMeter : 40;
    this._squareLength  = Number.isFinite(options.square_length) ? options.square_length : null;

    this._enabled   = false;
    this._percent01 = 0;
    this._intensity = 0;
    this._reachM    = 0;
    this._reachPx   = 0;
    this._durationS = 0.75;

    // Per-particle state: { glows, lastEmit, cooldownS, circle?, _particleRef }
    this._state = new Map();

    this._selectionSalt = (Math.random() * 1e9) | 0;

    // Throughput guard for pulses
    this._maxGlobalPulsesPerSecond = 300;
    this._globalPulseWindow = { t0: NOW_S(), count: 0 };

    // Debug UI
    this._debug = !!options.debug;
    this._debugDrawCircles = !!options.debugDrawCircles;
    this._debugCircleColor = Number.isFinite(options.debugCircleColor) ? (options.debugCircleColor >>> 0) : 0xffffff;
    this._debugCircleAlpha = 0.5;

    // Cutout target for ERASE holes (preferred)
    this._cutContainer  = options.cutContainer || null;
    this._useEraseBlend = options.useEraseBlend !== false; // default true

    // Optional PIXI app so we can auto-find FogHoles if caller didn’t setCutContainer
    this._app = options.app || null;

    // Listen to UI config events so the panel actually toggles/updates runtime
    this._onConfigEvent = (ev) => {
      const detail = ev?.detail ?? {};
      this.configure(detail);
    };
    try {
      window.addEventListener(this._configEventName, this._onConfigEvent);
    } catch {}
  }

  /** Try to auto-detect FogHoles in emitter tool’s fog.js setup */
  _ensureAutoCutContainer() {
    if (this._cutContainer || !this._app?.stage) return;
    try {
      const fogComposite = this._app.stage.getChildByName?.("FogComposite");
      const feathered    = fogComposite?.getChildByName?.("FogFeatheredLayer");
      const holes        = feathered?.getChildByName?.("FogHoles");
      if (holes) {
        this.setCutContainer(holes, { useEraseBlend: true });
        if (this._debug) console.log("[GlowManager] Auto-wired to FogHoles");
      }
    } catch {}
  }

  /**
   * Convert PIXI global/screen coordinates into image-space coordinates when the
   * stage has been translated/scaled (as in the VTT SpellOverlay).
   * If we can’t resolve anything sane, we just return the input point.
   */
  _toImageSpace(pt) {
    if (!pt) return null;
    try {
      const app =
        this._app ||
        (typeof window !== "undefined" && window.__PA_SPELL_APP
          ? window.__PA_SPELL_APP
          : null);
      const stage = app && app.stage;
      if (!stage) return pt;

      const sx = Number(stage.scale?.x) || 1;
      const sy = Number(stage.scale?.y) || 1;
      const ox = Number(stage.position?.x) || 0;
      const oy = Number(stage.position?.y) || 0;

      if (!Number.isFinite(sx) || !Number.isFinite(sy) || sx === 0 || sy === 0) {
        return pt;
      }

      const x = (pt.x - ox) / sx;
      const y = (pt.y - oy) / sy;
      if (!Number.isFinite(x) || !Number.isFinite(y)) return pt;

      return { x, y };
    } catch {
      return pt;
    }
  }

  /** Reconfigure with UI effectSettings + optional gridConfig */
  configure(effectSettings, gridConfig = {}) {
    const {
      enabled,
      percent01,
      intensity,
      reachM,
      reachPx,
      durationS,
    } = computeGlowParams(effectSettings || {}, this._pxPerMeter, gridConfig || {});

    this._enabled   = !!enabled;
    this._percent01 = Number(percent01) || 0;
    this._intensity = Number(intensity) || 0;
    this._reachM    = Math.max(0, Number(reachM) || 0);
    this._reachPx   = Math.max(0, Number(reachPx) || 0);
    this._durationS = Math.max(0, Number(durationS) || 0.06);

    // grid + legacy names
    if (Number.isFinite(gridConfig.pxPerMeter) && gridConfig.pxPerMeter > 0) {
      this._pxPerMeter = gridConfig.pxPerMeter;
    } else {
      const mpp = Number.isFinite(gridConfig.metre_in_pixels)
        ? gridConfig.metre_in_pixels
        : gridConfig.meter_in_pixels;
      if (Number.isFinite(mpp) && mpp > 0) this._pxPerMeter = mpp;
    }
    if (Number.isFinite(gridConfig.square_length) && gridConfig.square_length > 0) {
      this._squareLength = gridConfig.square_length;
    }

    // Change selection salt occasionally so percent selection reshuffles when user changes settings.
    this._selectionSalt = (this._selectionSalt + 1337) | 0;

    // Auto-wire to fog if possible
    this._ensureAutoCutContainer();

    // Recompute per-particle state so changes apply immediately
    for (const [id, st] of this._state.entries()) {
      st.cooldownS = cooldownWithDuration(this._intensity, this._durationS);

      const hasRadius = (this._reachM > 0 || this._reachPx > 0 || st?._particleRef?.__debugCircleRadius > 0);
      st.glows =
        this._enabled &&
        hasRadius &&
        this._intensity > 0 &&
        shouldParticleGlow(id, this._percent01, this._selectionSalt);

      if (this._circleShouldExist(st) || this._debugDrawCircles) {
        this._ensureOrUpdateCircleFor(id, st._particleRef);
      } else {
        this._removeCircle(st);
      }
    }

    if (this._debug) {
      console.log("[GlowManager] configure", {
        enabled: this._enabled, percent01: this._percent01, intensity: this._intensity,
        reachM: this._reachM, pxPerMeter: this._pxPerMeter,
        reachPx: this._reachPx, durationS: this._durationS
      });
    }
  }

  setGlowEventName(name) { if (typeof name === "string" && name.length) this._glowEventName = name; }
  setPxPerMeter(pxPerMeter) { if (Number.isFinite(pxPerMeter) && pxPerMeter > 0) this._pxPerMeter = pxPerMeter; }
  setDebug(flag) { this._debug = !!flag; }
  setDebugCircles(flag, color) {
    this._debugDrawCircles = !!flag;
    if (Number.isFinite(color)) this._debugCircleColor = color >>> 0;
    for (const [id, st] of this._state.entries()) {
      if (this._circleShouldExist(st) || this._debugDrawCircles) this._ensureOrUpdateCircleFor(id, st._particleRef);
      else this._removeCircle(st);
    }
  }

  /** Provide/replace the cutout container at runtime */
  setCutContainer(container, { useEraseBlend = true } = {}) {
    this._cutContainer = container || null;
    this._useEraseBlend = useEraseBlend !== false;
    for (const [id, st] of this._state.entries()) {
      if (this._circleShouldExist(st) || this._debugDrawCircles) this._ensureOrUpdateCircleFor(id, st._particleRef);
      else this._removeCircle(st);
    }
  }

  // ---------------------------------------------------------------------------
  // TEST HOOK: draw a center ERASE circle into FogHoles so you can verify cutting
  // ---------------------------------------------------------------------------
  debugCutCenterThroughFog(app, { radiusPx = 120, alpha = 1 } = {}) {
    const appRef = app || this._app;
    if (!appRef?.stage) return false;

    const fogComposite = appRef.stage.getChildByName?.("FogComposite");
    if (!fogComposite) return false;

    const feathered = fogComposite.getChildByName?.("FogFeatheredLayer");
    const holes = feathered?.getChildByName?.("FogHoles");
    if (!holes) return false;

    const w = appRef.renderer?.width ?? appRef.screen?.width ?? 0;
    const h = appRef.renderer?.height ?? appRef.screen?.height ?? 0;
    const cx = w * 0.5, cy = h * 0.5;

    const g = new PIXI.Graphics();
    g.name = "DebugFogHoleCircle";
    g.blendMode = (PIXI.BLEND_MODES.ERASE != null)
      ? PIXI.BLEND_MODES.ERASE
      : (PIXI.BLEND_MODES.DST_OUT ?? PIXI.BLEND_MODES.NORMAL);
    g.beginFill(0xffffff, 1);
    g.drawCircle(cx, cy, Math.max(2, radiusPx));
    g.endFill();
    g.alpha = alpha;

    holes.addChild(g);

    return true;
  }

  // ---- lifecycle ------------------------------------------------------------
  onParticleSpawn(particle, seedOverride = null) {
    const id = this._particleId(particle, seedOverride);
    if (id == null) return false;

    // Ensure we’re wired to fog if available
    this._ensureAutoCutContainer();

    const hasRadius =
      (this._reachM > 0 || this._reachPx > 0 || particle?.__debugCircleRadius > 0);

    const glows =
      this._enabled &&
      hasRadius &&
      this._intensity > 0 &&
      shouldParticleGlow(id, this._percent01, this._selectionSalt);

    const st = {
      glows,
      lastEmit: 0,
      cooldownS: cooldownWithDuration(this._intensity, this._durationS),
      _particleRef: particle,
      circle: null,
    };
    this._state.set(id, st);

    if (this._circleShouldExist(st) || this._debugDrawCircles) {
      this._ensureOrUpdateCircleFor(id, particle);
    } else {
      this._removeCircle(st);
    }

    if (this._debug) console.log("[GlowManager] spawn", { id, glows, cooldownS: st.cooldownS });
    return glows;
  }

  onParticleStep(particle, dt, alphaScale = 1) {
    const id = this._particleId(particle, null);
    if (id == null) return;

    const st = this._state.get(id);
    if (!st) return;

    // keep circle alive & positioned
    if (this._circleShouldExist(st) || this._debugDrawCircles) {
      this._ensureOrUpdateCircleFor(id, particle);
    } else {
      this._removeCircle(st);
    }

    if (!st.glows || !this._enabled) return;

    const now = NOW_S();
    if (!this._allowGlobalPulse(now)) return;

    const elapsed = now - (st.lastEmit || 0);
    if (elapsed < (st.cooldownS || 0.06)) return;

    const gp = this._getGlobalXY(particle);
    if (!gp) return;

    const ip = this._toImageSpace(gp) || gp;

    const alpha = Math.max(0, Math.min(1, (this._intensity || 0) * (Number(alphaScale) || 1)));
    if (alpha <= 0.00001) return;

    const payload = {
      x: ip.x, y: ip.y,
      radiusM: this._reachM,
      pxPerMeter: this._pxPerMeter,
      radiusPx: this._reachPx > 0 ? this._reachPx : (this._reachM * (this._pxPerMeter || 0)),
      alpha,
      durationS: this._durationS,
      los: true,
      fade: true,
    };

    dispatchGlowPulse(payload, this._glowEventName);
    st.lastEmit = now;
    this._bumpGlobalCounter(now);
  }

  emitOnceFor(particle, alphaScale = 1) {
    const gp = this._getGlobalXY(particle);
    if (!gp) return false;

    const ip = this._toImageSpace(gp) || gp;

    const alpha = Math.max(0, Math.min(1, (this._intensity || 0) * (Number(alphaScale) || 1)));
    if (alpha <= 0.00001) return false;

    const payload = {
      x: ip.x, y: ip.y,
      radiusM: this._reachM,
      pxPerMeter: this._pxPerMeter,
      radiusPx: this._reachPx > 0 ? this._reachPx : (this._reachM * (this._pxPerMeter || 0)),
      alpha,
      durationS: this._durationS,
      los: true,
      fade: true,
    };
    dispatchGlowPulse(payload, this._glowEventName);
    return true;
  }

  onParticleKill(particle, seedOverride = null) {
    const id = this._particleId(particle, seedOverride);
    if (id == null) return;
    const st = this._state.get(id);
    if (st) {
      this._removeCircle(st);
      this._state.delete(id);
    }
  }

  reset() {
    for (const st of this._state.values()) this._removeCircle(st);
    this._state.clear();
  }
  destroy() {
    this.reset();
    try { window.removeEventListener(this._configEventName, this._onConfigEvent); } catch {}
  }

  // ---- internals ------------------------------------------------------------
  _circleShouldExist(st) {
    if (!this._cutContainer) return false;
    if (!this._enabled) return false;
    if (!st?.glows) return false;
    const r = this._currentReachPx() || st?._particleRef?.__debugCircleRadius || 0;
    return r > 2;
  }

  _particleId(particle, seedOverride) {
    if (Number.isFinite(seedOverride)) return seedOverride | 0;
    if (!particle) return null;
    if (Number.isFinite(particle.id)) return particle.id | 0;
    if (Number.isFinite(particle.seed)) return particle.seed | 0;
    if (!particle.__gm_id) particle.__gm_id = (Math.random() * 2 ** 31) | 0;
    return particle.__gm_id;
  }

  _particleDisplayObject(particle) {
    return particle.displayObject || particle.sprite || particle.view || particle.do || particle;
  }

  _getGlobalXY(particle) {
    const obj = this._particleDisplayObject(particle);
    if (!obj || typeof obj.getGlobalPosition !== "function") return null;
    const pt = obj.getGlobalPosition();
    const x = Number(pt.x), y = Number(pt.y);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
    return { x, y, obj };
  }

  _allowGlobalPulse(now) {
    const w = this._globalPulseWindow;
    if (now - w.t0 >= 1) { w.t0 = now; w.count = 0; }
    return w.count < this._maxGlobalPulsesPerSecond;
  }
  _bumpGlobalCounter(now) {
    const w = this._globalPulseWindow;
    if (now - w.t0 >= 1) { w.t0 = now; w.count = 0; }
    w.count++;
  }

  _currentReachPx() {
    const viaM = (this._reachM * (this._pxPerMeter || 0)) || 0;
    return this._reachPx > 0 ? this._reachPx : viaM;
  }

  _ensureOrUpdateCircleFor(id, particle) {
    const st = this._state.get(id);
    if (!st) return;

    const obj = this._particleDisplayObject(particle);
    if (!obj || obj.destroyed) return;

    this._ensureAutoCutContainer();

    const targetRadius = Math.max(2, this._currentReachPx() || particle?.__debugCircleRadius || 0);

    if (!(this._circleShouldExist(st) || this._debugDrawCircles)) {
      this._removeCircle(st);
      return;
    }

    const parent = this._cutContainer || obj.parent;
    if (!parent) return;

    let g = st.circle;
    const needNew = !g || g.destroyed || g.parent !== parent;

    if (needNew) {
      this._removeCircle(st);
      g = new PIXI.Graphics();
      g.name = "GlowCutCircle";
      if (this._cutContainer && this._useEraseBlend && PIXI.BLEND_MODES?.ERASE != null) {
        g.blendMode = PIXI.BLEND_MODES.ERASE;
      } else if (this._cutContainer && PIXI.BLEND_MODES?.DST_OUT != null) {
        g.blendMode = PIXI.BLEND_MODES.DST_OUT;
      } else {
        g.blendMode = PIXI.BLEND_MODES.NORMAL;
      }
      parent.addChild(g);
      st.circle = g;
    }

    g.clear();
    if (this._cutContainer) {
      g.beginFill(0xffffff, 1);
      g.drawCircle(0, 0, targetRadius);
      g.endFill();
      g.alpha = 1;
    } else {
      g.beginFill(this._debugCircleColor, this._debugCircleAlpha);
      g.drawCircle(0, 0, targetRadius);
      g.endFill();
      g.alpha = this._debugCircleAlpha;
    }

    if (this._cutContainer) {
      const gp = obj.getGlobalPosition();
      const lp = parent.toLocal(gp);
      g.position.set(lp.x, lp.y);
    } else {
      g.position.set(obj.x, obj.y);
      if ("zIndex" in g) g.zIndex = (obj.zIndex ?? 0) - 1;
    }
  }

  _removeCircle(st) {
    if (st && st.circle) {
      try {
        const g = st.circle;
        const par = g.parent;
        if (par) par.removeChild(g);
        g.destroy(true);
      } catch {}
      st.circle = null;
    }
  }
}
